import React, { FC, useMemo } from 'react';

import { Button, AssetWithIcon, Text, TooltipWrapper } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';
import { useInputBase } from '@/widgets/Modals';
import { formatSmart } from '@/shared/utils/helpers';
import { NumericInput } from '@/widgets/NumericInput';

export type WithdrawalInputBaseProps = {
    value: string;
    name: string;
    setValue: (value: string | null) => void;
    poolBalance: string;
    tokenSymbol: string;
    disabled: boolean;
    decimals: number;
    maxBalance: string;
    interestClaimable: string;
    totalPosition: string;
    interestAccrued: string;
};

export const WithdrawalText = {
    Withdrawable: 'Withdrawable',
    InterestClaimable: 'Interest Claimable:',
    TotalPosition: 'Total Position:',
    InterestAccrued: 'Interest Accrued:',
    Tooltips: {
        Withdrawable:
            "Amount available for withdrawal now. If it's less than the total position, a portion has been allocated and is earning interest",
        ClaimableInterest: 'Claimable Interest: Accrued Interest − Pending Interest',
        InterestAccrued:
            'Interest Accrued is calculated based on your deposit, pool utilization, APY, and the time period',
    },
};

export const WithdrawalInputBase: FC<WithdrawalInputBaseProps> = ({
    value,
    name,
    setValue,
    poolBalance,
    tokenSymbol,
    disabled,
    decimals,
    maxBalance,
    interestClaimable,
    totalPosition,
    interestAccrued,
}) => {
    const { isError, handleAmountChange, handleMaxClick } = useInputBase({
        name,
        balance: poolBalance,
        errorMessage: 'Amount is more than balance',
        decimals,
    });

    return (
        <>
            <div
                className={cn(
                    'bg-background-secondary border border-border-opaque rounded-lg p-5 flex flex-col',
                    isError && 'border-content-negative',
                )}
            >
                <div className='flex justify-between items-center [&_input]:p-0 [&_input]:h-[28px]'>
                    <NumericInput
                        value={value}
                        onRawValueChange={async (rawValue) => {
                            await handleAmountChange(rawValue, setValue);
                        }}
                        decimals={decimals}
                        disabled={disabled}
                        name={name}
                        className='focus-visible:outline-none text-[20px] !font-[Inter] font-regular placeholder:text-content-secondary bg-transparent border-none outline-0 focus-visible:ring-0 !p-0 text-content-primary shadow-none !h-[28px]'
                    />
                    <div className='flex justify-end gap-2 min-w-[180px]'>
                        <AssetWithIcon
                            assetName={tokenSymbol}
                            withTooltip
                            className='text-xl font-light [&_img]:h-[28px] [&_img]:w-[28px [&_div]:max-w-[120px] [&_div]:text-ellipsis [&_div]:overflow-hidden [&_div]:whitespace-nowrap'
                        />
                    </div>
                </div>
            </div>

            <WithdrawalInputError isError={isError} />

            <div className='flex justify-between mt-3 text-sm'>
                <WithdrawalMaxAvailable
                    maxBalance={maxBalance}
                    setValue={setValue}
                    handleMaxClick={handleMaxClick}
                    tooltipText={WithdrawalText.Tooltips.Withdrawable}
                />
                <WithdrawalSummaryRow
                    text={WithdrawalText.InterestClaimable}
                    value={interestClaimable}
                    tooltipText={WithdrawalText.Tooltips.ClaimableInterest}
                />
            </div>

            <div className='flex justify-between mt-3 text-sm'>
                <WithdrawalSummaryRow text={WithdrawalText.TotalPosition} value={totalPosition} />
                <WithdrawalSummaryRow
                    text={WithdrawalText.InterestAccrued}
                    value={interestAccrued}
                    tooltipText={WithdrawalText.Tooltips.InterestAccrued}
                />
            </div>
        </>
    );
};

export type WithdrawalInputErrorProps = {
    isError: { message: string } | null;
};

export const WithdrawalInputError: FC<WithdrawalInputErrorProps> = ({ isError }) => {
    return isError && <div className='text-content-negative text-sm mt-1'>{isError.message}</div>;
};

export type WithdrawalMaxAvailableProps = {
    maxBalance: string;
    handleMaxClick: (setValue: (value: string) => void, maxBalance: string) => void;
    setValue: (value: string) => void;
    tooltipText?: string;
};

export const WithdrawalMaxAvailable: FC<WithdrawalMaxAvailableProps> = ({
    maxBalance,
    handleMaxClick,
    setValue,
    tooltipText,
}) => {
    return (
        <div className='flex items-center gap-1'>
            <Text
                type={Text.types.Paragraph}
                className='text-content-tertiary font-medium'
                name='withdrawal-max-available-label'
            >
                {WithdrawalText.Withdrawable}:
            </Text>
            <Button
                variant={Button.types.Link}
                type='button'
                name='withdrawal-max-available'
                onClick={() => handleMaxClick(setValue, maxBalance)}
                className='text-content-accent p-0 ml-2 rounded-none max-h-[20px]'
            >
                {formatSmart(maxBalance)}
            </Button>
            {tooltipText ? <TooltipWrapper text={tooltipText} /> : null}
        </div>
    );
};

export type WithdrawalSummaryRowProps = {
    text: string;
    value: string;
    tooltipText?: string;
};

export const WithdrawalSummaryRow: FC<WithdrawalSummaryRowProps> = ({
    text,
    value,
    tooltipText,
}) => {
    const { textId, valueId } = useMemo(() => {
        let textId = '';
        let valueId = '';

        if (text) {
            textId = text.toLowerCase().replace(/ /g, '-').replace(/:/g, '');
            textId += '-label';

            valueId = text.toLowerCase().replace(/ /g, '-').replace(/:/g, '');
            valueId += '-value';
        }

        return { textId, valueId };
    }, [text]);

    return (
        <div className='flex justify-between text-md items-center'>
            <Text type={Text.types.Paragraph} className='text-content-tertiary mr-3' name={textId}>
                {text}
            </Text>
            <Text
                type={Text.types.Paragraph}
                fontWeight={Text.weights.SemiBold}
                className={cn({
                    'mr-3': tooltipText,
                })}
                name={valueId}
            >
                {formatSmart(value)}
            </Text>
            {tooltipText ? <TooltipWrapper text={tooltipText} /> : null}
        </div>
    );
};
